import { FolderInputIcon } from "lucide-react";

import { useI18n } from "~/i18n";
import { cn } from "~/lib/utils";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import type { CustomVaultFolder } from "./vaultFolders";

export function VaultFileFolderMenu({
  itemName,
  customFolders,
  assignedFolderId,
  onAssign,
  className,
  iconClassName,
}: {
  itemName: string;
  customFolders: readonly CustomVaultFolder[];
  assignedFolderId: string | undefined;
  onAssign: (folderId: string | null) => void;
  className?: string;
  iconClassName?: string;
}) {
  const { t } = useI18n();

  if (customFolders.length === 0) return null;

  return (
    <Menu>
      <MenuTrigger
        render={
          <button
            type="button"
            aria-label={`${t("vault.moveToFolder")} ${itemName}`}
            // A hover tooltip as well as the label: this is an icon-only button that only appears
            // on hover, so sighted users had nothing telling them it files the item.
            title={t("vault.moveToFolder")}
            className={cn(
              "flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
              className,
            )}
          >
            <FolderInputIcon className={cn("size-4", iconClassName)} aria-hidden="true" />
          </button>
        }
      />
      <MenuPopup align="end" className="min-w-44">
        {customFolders.map((folder) => (
          <MenuItem
            key={folder.id}
            onClick={() => onAssign(folder.id)}
            aria-current={assignedFolderId === folder.id ? "true" : undefined}
          >
            {folder.name}
          </MenuItem>
        ))}
        {assignedFolderId ? (
          <MenuItem onClick={() => onAssign(null)}>{t("vault.removeFromFolder")}</MenuItem>
        ) : null}
      </MenuPopup>
    </Menu>
  );
}
